import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import type { DropResult } from 'react-beautiful-dnd';

import {
  IQuestionsTemplate,
  ISurveyAnsweredDetail,
  ISurveyTemplate,
  SurveyTemplatesResponse,
  TypeComponentTemplate
} from 'api/insight';
import { useGlobalContext } from 'context/GlobalContext';
import { AccessElements } from 'helpers/app.config';

import SurveyMutationCRUD from '../services/surveyMutationCRUD';
import { ISurveyQuestion } from '../components/ContentBox';
import SurveySearchService from '../services/surveySearch';

interface IProps {
  idTemplate?: number;
  IdTicket?: number;
  onCloseModal?: () => void;
  isDeleting?: boolean;
  surveyAnswered?: {
    title?: string;
    description: string;
    questionsAndAnswers: ISurveyAnsweredDetail[];
  };
}

const dict: { [key: string]: string } = {
  [TypeComponentTemplate.linearScale]: 'rating',
  [TypeComponentTemplate.severalOptions]: 'select'
};
function useSurveyQuestions({
  idTemplate,
  IdTicket,
  onCloseModal,
  isDeleting = false,
  surveyAnswered
}: IProps) {
  const form = useForm<{
    title?: string;
    description?: string;
    [`key-label`]?: string;
  }>({
    mode: 'onChange'
  });

  const [surveyQuestions, setSurveyQuestions] = useState<
    (ISurveyQuestion & { label?: string })[]
  >([]);
  const [surveyQuestionsTicket, setSurveyQuestionsTicket] = useState<
    ISurveyQuestion[]
  >([]);
  const navigate = useNavigate();
  const { hasPerm } = useGlobalContext();

  const callbackSuccess = useCallback(() => {
    form.reset({ title: null, description: null });
    setSurveyQuestions([]);
    if ((idTemplate || isDeleting) && onCloseModal) {
      onCloseModal();
      return;
    }
    if (hasPerm([AccessElements.SurveySearch])) navigate(`/surveys/search`);
  }, [form, hasPerm, idTemplate, isDeleting, navigate, onCloseModal]);

  const { surveyTemplateById, surveyTemplateByIdTicket } = SurveySearchService({
    idTemplate,
    IdTicket
  });
  const { create, update, isLoading } = SurveyMutationCRUD({
    isDeleting,
    callbackCreateSuccess: callbackSuccess,
    callbackUpdateSuccess: callbackSuccess
  });

  const settingAnswers = useCallback(
    (
      orderItem: string | number,
      type: TypeComponentTemplate,
      value: string | number
    ) => {
      form.setValue(`${orderItem}-${dict[type] || type}` as `key-label`, value);
    },
    [form]
  );

  useMemo(() => {
    form.reset({ title: null, description: null });

    if (surveyAnswered?.questionsAndAnswers?.length) {
      form.setValue('title', surveyAnswered?.title);
      form.setValue('description', surveyAnswered?.description);
      const surveysQTemplates = surveyAnswered?.questionsAndAnswers?.map(
        (item) => {
          const newItem: IQuestionsTemplate & {
            valueResponse: number | string;
          } = {
            ...item,
            ...item?.questions?.[0]
          };
          form.setValue(
            `${newItem?.orderItem}-label` as 'key-label',
            newItem?.label
          );
          settingAnswers(
            newItem?.orderItem,
            newItem?.typeComponent,
            newItem?.valueResponse
          );

          return {
            ...newItem,
            orderItem: String(newItem?.orderItem),
            ...(newItem?.options &&
            (newItem.typeComponent === TypeComponentTemplate.linearScale ||
              newItem.typeComponent === TypeComponentTemplate.severalOptions)
              ? {
                  options: JSON.parse(newItem.options) as
                    | string
                    | { label: string; id: string }[]
                }
              : {})
          };
        }
      );
      setSurveyQuestions(surveysQTemplates as ISurveyQuestion[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, surveyAnswered?.questionsAndAnswers?.length]);

  useMemo(() => {
    form.reset({ title: null, description: null });
    if (surveyTemplateById?.data) {
      const { title, description, surveyQuestionsTemplate } =
        surveyTemplateById.data as SurveyTemplatesResponse;
      form.setValue('title', title);
      form.setValue('description', description);

      if (surveyQuestionsTemplate?.length) {
        const surveysQTemplates = surveyQuestionsTemplate.map((item) => {
          form.setValue(`${item?.orderItem}-label` as 'key-label', item?.label);
          return {
            ...item,
            orderItem: String(item?.orderItem),
            ...(item?.options &&
            (item.typeComponent === TypeComponentTemplate.linearScale ||
              item.typeComponent === TypeComponentTemplate.severalOptions)
              ? {
                  options: JSON.parse(item.options) as
                    | string
                    | { label: string; id: string }[]
                }
              : {})
          };
        });
        setSurveyQuestions(surveysQTemplates as ISurveyQuestion[]);
      }
    }
  }, [form, surveyTemplateById?.data]);

  useMemo(() => {
    form.reset({ title: null, description: null });
    if (surveyTemplateByIdTicket?.data) {
      const { title, description, surveyQuestionsTemplate } =
        surveyTemplateByIdTicket.data as SurveyTemplatesResponse;
      form.setValue('title', title);
      form.setValue('description', description);

      if (surveyQuestionsTemplate?.length) {
        const surveysQTemplates = surveyQuestionsTemplate.map((item) => {
          form.setValue(`${item?.orderItem}-label` as 'key-label', item?.label);
          return {
            ...item,
            orderItem: String(item?.orderItem),
            ...(item?.options &&
            (item.typeComponent === TypeComponentTemplate.linearScale ||
              item.typeComponent === TypeComponentTemplate.severalOptions)
              ? {
                  options: JSON.parse(item.options) as
                    | string
                    | { label: string; id: string }[]
                }
              : {})
          };
        });
        setSurveyQuestionsTicket(surveysQTemplates as ISurveyQuestion[]);
      }
    }
  }, [form, surveyTemplateByIdTicket?.data]);

  const handleSubmit = (values: { title?: string; description?: string }) => {
    const { title, description } = values;
    if (!title || !surveyQuestions?.length) {
      return;
    }
    const questionsTemplate = surveyQuestions?.map((question, idx) => ({
      ...question,
      options: JSON.stringify(question.options),
      orderItem: idx + 1
    })) as IQuestionsTemplate[];

    const fields: ISurveyTemplate = {
      title,
      description,
      questionsTemplate
    };

    if (idTemplate) update.mutate({ ...fields, enabled: true, idTemplate });
    else create.mutate(fields);
  };

  const handleDelete = useCallback(
    (data: SurveyTemplatesResponse) => {
      const fields: ISurveyTemplate = {
        title: data?.title,
        description: data?.description,
        idTemplate: data?.idSurveyTemplate,
        questionsTemplate: data?.surveyQuestionsTemplate,
        enabled: false
      };
      if (fields?.idTemplate) update.mutate(fields);
    },
    [update]
  );

  const handleSelectedNewOption = useCallback(
    (opt: TypeComponentTemplate) => {
      setSurveyQuestions((prev) => {
        form.setValue(`${prev.length + 1}-label` as 'key-label', '');
        return [
          ...prev,
          {
            orderItem: `${prev.length + 1}`,
            label: '',
            typeComponent: opt,
            isRequired: true
          }
        ];
      });
    },
    [form]
  );

  const handleCancel = useCallback(() => {
    form.reset({ title: null, description: null });
    setSurveyQuestions([]);
    if (!idTemplate) {
      navigate(`/surveys/search`);
      return;
    }
    if (onCloseModal) onCloseModal();
  }, [form, idTemplate, navigate, onCloseModal]);

  const handleChangeSelected = useCallback(
    (opt: TypeComponentTemplate, id: string) => {
      form.setValue(`${id}-label` as 'key-label', '');
      setSurveyQuestions((prev) =>
        prev.map((q) =>
          q.orderItem === id
            ? { ...q, options: '', label: '', typeComponent: opt }
            : q
        )
      );
    },
    [form]
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const updatedQuestions = [...surveyQuestions];
    const [removed] = updatedQuestions.splice(result.source.index, 1);
    updatedQuestions.splice(result.destination.index, 0, removed);

    setSurveyQuestions(updatedQuestions);
  };

  const handleRemoveCard = useCallback(
    (id: string) => {
      setSurveyQuestions((prev) => {
        const newQuestions = prev.reduce((acc, item) => {
          const orderItemNum = Number(item.orderItem); // Convertir a número

          if (orderItemNum === Number(id)) {
            form.resetField(`${id}-label` as 'key-label');
            return acc; // No añadir el ítem a la nueva lista
          }

          // Ajustar el orderItem solo si el ítem actual tiene un orderItem mayor al que se está eliminando
          const newOrder =
            orderItemNum > Number(id)
              ? (orderItemNum - 1).toString()
              : item.orderItem;
          const newItem = {
            ...item,
            orderItem: newOrder
          };
          if (orderItemNum > Number(id)) {
            form.setValue(`${newOrder}-label` as 'key-label', item.label);
          }
          acc.push(newItem);
          return acc;
        }, [] as typeof prev);

        return newQuestions;
      });
    },
    [form]
  );

  const handleRequired = useCallback((id: string, checked: boolean) => {
    setSurveyQuestions((prev) =>
      prev.map((q) => (q.orderItem === id ? { ...q, isRequired: checked } : q))
    );
  }, []);

  const handleChangeInputs = (
    id: string,
    key: 'label' | 'options',
    value: string | number
  ) =>
    setSurveyQuestions((prev) =>
      prev.map((q) =>
        q?.orderItem === id
          ? {
              ...q,
              orderItem: q?.orderItem,
              typeComponent: q?.typeComponent,
              [key]: value
            }
          : q
      )
    );

  return {
    form,
    surveyQuestions,
    surveyQuestionsTicket,
    handleSubmit,
    handleSelectedNewOption,
    handleCancel,
    handleChangeSelected,
    handleDragEnd,
    handleRemoveCard,
    handleRequired,
    handleChangeInputs,
    handleDelete,
    isLoading,
    isLoadingTemplates: surveyTemplateById?.isLoading
  };
}

export default useSurveyQuestions;
